import React, { useState } from 'react';
import { useStore } from '../state/store';
import { Card, Chip, Banner, Empty, SectionHead, Area, Modal, KV } from '../components/ui';
import { DecisionForm, ExceptionForm } from '../components/Forms';
import { fmtDate } from '../lib/dates';
import { fmtMoney } from '../lib/derive';
import { EMPTY_COPY } from '../lib/constants';

export default function Decisions() {
  const { state, d, patch, remove, today, set } = useStore();
  const [modal, setModal] = useState(null);
  const [reviewing, setReviewing] = useState(null);

  const exceptions = state.exceptions || [];
  const pending = exceptions.filter((e) => !e.decision || e.decision === 'Pending');
  const decisions = [...(state.decisions || [])].sort((a, b) => (a.date < b.date ? 1 : -1));
  const closeouts = [...(state.closeouts || [])].sort((a, b) => (a.date < b.date ? 1 : -1));
  const sprintReviews = [...(state.sprintReviews || [])].sort((a, b) =>
    String(b.sprintStart || '').localeCompare(String(a.sprintStart || '')),
  );
  const lockedRange = d?.sprint?.rangeLabel || 'this sprint';

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>Decisions</h2>
          <Chip>{decisions.length} logged</Chip>
          {pending.length > 0 && <Chip tone="red">{pending.length} exception(s) pending</Chip>}
          <div className="spacer" />
          <button className="btn sm" onClick={() => setModal('exception')}>Request Strategy Exception</button>
          <button className="btn sm primary" onClick={() => setModal('decision')}>Log decision</button>
        </div>
        <div className="sub">Permanent record. Strategy decisions never leave this log.</div>
      </div>

      <section className="section">
        <SectionHead title="Strategy exceptions" hint={`The strategy is locked ${lockedRange}. This is the audit trail.`} />
        {exceptions.length === 0 ? (
          <Empty title="No exceptions requested.">
            That is the expected state. The lock exists so that interest in a new market has to survive a form.
          </Empty>
        ) : (
          <div className="grid g2">
            {exceptions.map((x) => (
              <Card
                key={x.id}
                title={x.trigger}
                sub={fmtDate(x.date, { year: true })}
                right={
                  <Chip tone={x.decision === 'Approved' || x.decision === 'Approve' ? 'green' : x.decision === 'Rejected' || x.decision === 'Reject' ? 'red' : x.decision === 'Deferred' || x.decision === 'Defer' ? 'amber' : ''}>
                    {x.decision || 'Pending'}
                  </Chip>
                }
              >
                <KV
                  items={[
                    ['Evidence', x.evidence],
                    ['Expected upside', x.upside],
                    ['Cash cost', fmtMoney(x.costCash, state.settings?.currency)],
                    ['Founder hours', x.costHours],
                    ['Product work', x.costProduct],
                    ['Legal work', x.costLegal],
                    ['What stops', x.whatStops],
                  ]}
                />
                {(!x.decision || x.decision === 'Pending') && (
                  <>
                    <div className="hr" />
                    <div className="row tight">
                      {[
                        { value: 'Approved', label: 'Approve' },
                        { value: 'Rejected', label: 'Reject' },
                        { value: 'Deferred', label: 'Defer' },
                      ].map((dcs) => (
                        <button
                          key={dcs.value}
                          className={`btn sm ${dcs.value === 'Approved' ? 'primary' : ''}`}
                          onClick={() => setReviewing({ id: x.id, decision: dcs.value })}
                        >
                          {dcs.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {x.rationale && (
                  <div style={{ marginTop: 10 }}>
                    <Banner>{x.rationale}</Banner>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <SectionHead title="Decision log" hint="Question · options · evidence · decision · review date." />
        {decisions.length === 0 ? (
          <Empty title={EMPTY_COPY.decisions}>Log the first one now — the pricing or scope call you already made this week.</Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 95 }}>Date</th>
                    <th style={{ width: 110 }}>Type</th>
                    <th>Question</th>
                    <th>Decision</th>
                    <th>Evidence relied on</th>
                    <th style={{ width: 95 }}>Review</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {decisions.map((dec) => {
                    const due = dec.reviewDate && dec.reviewDate <= today;
                    return (
                      <tr key={dec.id}>
                        <td className="num">{fmtDate(dec.date)}</td>
                        <td><Chip>{dec.type}</Chip></td>
                        <td>{dec.question}<div className="dim tiny">{dec.options}</div></td>
                        <td>{dec.decision}</td>
                        <td className="dim tiny">{dec.evidence || '—'}</td>
                        <td className="num">{due ? <span className="chip amber">due</span> : fmtDate(dec.reviewDate)}</td>
                        <td className="actions">
                          <button className="btn sm ghost" onClick={() => remove('decisions', dec.id)}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section className="section">
        <SectionHead title="Sprint post-mortems" hint="Lessons keyed by T0. Survive re-baseline; feed the next sprint." />
        {sprintReviews.length === 0 ? (
          <Empty title={EMPTY_COPY.sprintReviews}>Close Day 45 on Gates, or complete an early cycle-close in Settings.</Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 95 }}>T0</th>
                    <th style={{ width: 80 }}>Ended</th>
                    <th style={{ width: 70 }}>Option</th>
                    <th>Carry-forward</th>
                    <th>Proved / killed</th>
                    <th style={{ width: 90 }}>Again?</th>
                  </tr>
                </thead>
                <tbody>
                  {sprintReviews.map((r) => (
                    <tr key={r.id}>
                      <td className="num">{fmtDate(r.sprintStart)}</td>
                      <td className="num">
                        {fmtDate(r.endedAt)}
                        {r.earlyClose ? <div className="dim tiny">early</div> : null}
                      </td>
                      <td>{r.option ? <Chip>{r.option}</Chip> : <span className="dim">—</span>}</td>
                      <td>{r.carryForward || <span className="dim">—</span>}</td>
                      <td className="dim tiny">{r.proved || '—'}</td>
                      <td>
                        {r.runAgain === true ? 'Yes' : r.runAgain === false ? 'No' : '—'}
                        {r.runAgainWhy ? <div className="dim tiny">{r.runAgainWhy}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section className="section">
        <SectionHead title="Daily closeouts" hint="What was acquired, what moved, what produced nothing." />
        {closeouts.length === 0 ? (
          <Empty title={EMPTY_COPY.closeout}>Press C anywhere in the app to close the day.</Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 95 }}>Date</th>
                    <th>Evidence acquired</th>
                    <th>Moved toward E4/E5</th>
                    <th>Produced no evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {closeouts.map((c) => (
                    <tr key={c.id}>
                      <td className="num">{fmtDate(c.date)}</td>
                      <td>{c.q1 || <span className="dim">nothing recorded</span>}</td>
                      <td>{c.q2 || <span className="dim">—</span>}</td>
                      <td>{c.q3 || <span className="dim">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {modal === 'decision' && <DecisionForm onClose={() => setModal(null)} />}
      {modal === 'exception' && <ExceptionForm onClose={() => setModal(null)} />}
      {reviewing && (
        <ExceptionDecision
          decision={reviewing.decision}
          onClose={() => setReviewing(null)}
          onSave={(rationale) => {
            patch('exceptions', reviewing.id, { decision: reviewing.decision, decidedAt: today, rationale });
            const trigger = String(reviewing.trigger || '');
            const bumpsLock =
              reviewing.decision === 'Approved' &&
              (/contaminat|recompile|Wrong-tenant|Plan recompile/i.test(trigger) ||
                trigger === 'Wrong-tenant / contaminated strategy template' ||
                trigger === 'Plan recompile');
            if (bumpsLock) {
              set('settings.strategyApprovedAt', String(today).slice(0, 10));
            }
          }}
        />
      )}
    </>
  );
}

function ExceptionDecision({ decision, onClose, onSave }) {
  const [rationale, setRationale] = useState('');
  return (
    <Modal
      title={`${decision} this exception`}
      sub="The rationale is permanent."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!rationale.trim()} onClick={() => { onSave(rationale); onClose(); }}>
            Record {decision.toLowerCase()}
          </button>
        </>
      }
    >
      <Area
        label="Rationale"
        value={rationale}
        onChange={setRationale}
        rows={4}
        placeholder={
          decision === 'Approved' || decision === 'Approve'
            ? 'What external evidence justified breaking the lock, and what stopped in exchange?'
            : 'Why the evidence did not meet the threshold.'
        }
      />
    </Modal>
  );
}
